import { toISOString } from '../../utils/localeHelpers';
import { exportFile, GetItemsProps } from 'medere-ui-framework';
import { axiosInstance } from '../../App';
import { AccountingAccount } from '../../interface/interface';
import { AxiosResponse } from 'axios';

export function findAAById({ accountingAccountId }: { accountingAccountId: number }) {
    return axiosInstance.get(`/api/secured/accounting/findAAById`, {
        params: {
            accountingAccountId
        }
    }).then(response => response.data)

}

export function findAccountingById({ accountingId }: { accountingId: number }) {
    return axiosInstance.get(`/api/secured/accounting/findAccountingById`, {
        params: {
            accountingId,
        }
    }).then(response => response.data)
}


export function getAllCompanies({ offSet, pageSize }: GetItemsProps) {
    return axiosInstance.get(`/api/secured/accounting/getAllCompanies`, {
        params: {
            offset: offSet,
            pageSize
        }
    }).then(resp => resp.data)
}

export function getAllAccountings({ offSet, pageSize }: GetItemsProps) {
    return axiosInstance.get(`/api/secured/accounting/getAllAccountings`, {
        params: {
            offSet,
            pageSize,
        }
    }).then(resp => resp.data)
}

export function addAccountingAccount(data: any) {
    return axiosInstance.post(`/api/secured/accounting/automaticAccountingAccount`, data).then(response => response.data)
}

export function getAllAccountingAccountsById(offset: number, pageSize: number, params: any) {
    return axiosInstance.get(`/api/secured/accounting/findAllAccountingAccountsById`, {
        params: {
            offset,
            pageSize,
            accountingId: params.id
        }
    }).then(response => response.data)
}

export function getAllAccountingAccountsByLevel({ filterValue, offSet, pageSize, params }: GetItemsProps): Promise<AccountingAccount> {
    return axiosInstance.post(`/api/secured/accounting/findAllAccountingAccountsByLevel`, null, {
        params: {
            inputText: filterValue ?? '',
            offSet,
            pageSize,
            level: params?.level,
            accounting: params?.accounting ?? -1,
            administrativeForm: params?.administrativeForm,
        }
    }).then(response => response.data)
}

export function getAllAccountingAccountsWithoutAllowedMovements({ filterValue, offSet, pageSize, params }: GetItemsProps): Promise<AccountingAccount> {
    return axiosInstance.post(`/api/secured/accounting/findAllAccountingAccountsWithoutAllowedMovements`, null, {
        params: {
            inputText: filterValue ?? '',
            offSet,
            pageSize,
            accounting: params?.accounting ?? -1,
            administrativeForm: params?.administrativeForm,
        }
    }).then(response => response.data)
}

export function getAllAccountingAccountsByAccountingAccount({ filterValue, offSet, pageSize, params }: GetItemsProps) {
    return axiosInstance.post(`/api/secured/accounting/getAllAccountingAccountsByAccountingAccount`, null, {
        params: {
            inputText: filterValue ?? '',
            offSet,
            pageSize,
            accounting: params?.accounting,
            parentAccount: params.parentAccount,
        }
    }).then(response => response.data)
}

export function findParentAAByChildrenAA({ accountingAccountId }: { accountingAccountId: number }) {
    return axiosInstance.get(`/api/secured/accounting/findParentAAByChildrenAA`, {
        params: {
            childrenAA: accountingAccountId,
        }
    }).then(response => response.data)
}

// _parentsAccount es un array de objs compuesto por los hijos directos del obj que se esta iterando
// _MyParentAccount, es el obj padre del obj que se esta iterando, es decir, el obj que se esta iterando es hijo de _MyParentAccount
/**
 * Convierte una lista de cuentas contables en una lista de elementos con estructura de árbol.
 * @param data - Lista de cuentas contables.
 * @param _parentsAccount - Lista de cuentas contables padres.
 * @param _myParentAccount - Cuenta contable padre actual.
 * @returns Lista de elementos con estructura de árbol.
 */
export function castAccountingsToItems(data: [], _parentsAccount: any[], _myParentAccount: any) {

    let item = {}
    let subItems: any = []
    _parentsAccount.forEach((parent: any) => {
        let _childrenAccount = []
        _childrenAccount = data.filter((children: any) => {
            return children.parentaccountingaccountpk == parent.accountingaccount
        })

        item = {
            key: parent.accountingaccount.toString(),
            caption: `${parent.identifier} - ${parent.description}`,
            description: parent.description,
            icon: null,
            className: "subItem",
            allowsMovement: parent.allowsmovements,
            label: parent.description,
            identifier: parent.identifier,
            alias: parent.alias,
            level: _myParentAccount?.level != null ? _myParentAccount.level + 1 : 1,
        }
        if (_childrenAccount.length > 0) {
            item = {
                ...item, children: castAccountingsToItems(data, _childrenAccount, item)
            }
        }
        subItems.push(item)
    })
    // Ordena la lista de SubItems por el identificador ("1.1","1.2.1","1.2.2")
    subItems.sort((a: any, b: any) => {
        const identifierA = a.identifier?.split('.').map(Number);
        const identifierB = b.identifier?.split('.').map(Number);

        for (let i = 0; i < Math.max(identifierA?.length, identifierB?.length); i++) {
            if (identifierA[i] === undefined) return -1;
            if (identifierB[i] === undefined) return 1;
            if (identifierA[i] < identifierB[i]) return -1;
            if (identifierA[i] > identifierB[i]) return 1;
        }

        return 0;
    })
    return subItems
}

export function registerMonthAccounting({ closingDate, accountingId }: { closingDate: Date | null, accountingId: number }) {
    return axiosInstance.get(`/api/secured/accounting/registerMonth`, {
        params: {
            accountingId,
            closingDate: closingDate ? toISOString(closingDate) : null,
        }
    })
}

export function getAllAccountingsPeriod(offset: number, pageSize: number, params: any) {
    return axiosInstance.get(`/api/secured/accounting/getAllAccountingsPeriod`, {
        params: {
            offset,
            pageSize,
        }
    }).then(response => response.data)
}



export function getLastAccountingPeriod({ offset, pageSize }: { offset: number, pageSize: number }) {
    return axiosInstance.get(`/api/secured/accounting/getLastAccountingPeriod`, {
        params: {
            offset,
            pageSize,
        }
    }).then(response => response.data)
}

export function dateIsNotSameOrBeforeLastAccountingPeriod({ closingDate }: { closingDate: Date | null }) {
    return axiosInstance.get(`/api/secured/accounting/dateIsNotSameOrBeforeLastAccountingPeriod`, {
        params: {
            closingDate: closingDate ? toISOString(closingDate) : null
        }
    }).then(response => response.status)
}

export function getAccountingMovementTypes({ filterValue, offSet, pageSize }: GetItemsProps) {
    return axiosInstance.get(`/api/secured/accounting/getAccountingMovementTypes`, {
        params: {
            offSet,
            pageSize,
            inputText: filterValue ?? ''
        }
    }).then(resp => resp.data)
}

export function findAccountingMovementTypeByAMIdAndAMDId(accountingMovementDetailId: number, accountingMovementId: number) {
    return axiosInstance.get(`/api/secured/accounting/findAccountingMovementTypeByAMIdAndAMDId`, {
        params: {
            accountingMovementDetailId,
            accountingMovementId,
        }
    }).then(response => response.data)
}

export function saveAccountingMovementDetail(data: any) {
    return axiosInstance.post(`/api/secured/accounting/saveAccountingMovementDetail`, data).then(response => response.data)
}

export function saveAccountingMovement(data: any) {
    return axiosInstance.post(`/api/secured/accounting/saveAccountingMovement`, data).then(response => response.data)
}

export function removeAccountingMovement(accountingMovementId: number) {
    return axiosInstance.delete(`/api/secured/accounting/removeAccountingMovementDetail`, {
        params: {
            accountingMovementId
        }
    }).then(response => response.data)
}

export function getAccountingMovements(offset: number, pageSize: number, params: any) {
    return axiosInstance.get(`/api/secured/accounting/getAccountingMovements`, {
        params: {
            accountingDate: params.accountingDate,
            accountingMovementTypeId: params.accountingMovementType,
            manual: params.manual,
            offset,
            pageSize
        }
    }).then(response => response.data)
}

export function getAccountingMovementById(accountingMovementId: number) {
    return axiosInstance.get(`/api/secured/accounting/getAccountingMovementById`, {
        params: {
            accountingMovementId
        }
    }).then(response => response.data)
}

export function getAccountingMovementDetailsByAccountingMovementId(accountingMovementId: number) {
    return axiosInstance.get(`/api/secured/accounting/getAccountingMovementDetailsByAccountingMovementId`, {
        params: {
            accountingMovementId
        }
    }).then(response => response.data)
}

export function getTSumBalance(_offset: number, _pageSize: number, params: any) {
    return axiosInstance.get(`/api/secured/accounting/getTSumBalance`, {
        params: {
            fromDate: params.fromDate,
            toDate: params.toDate,
            offset: 0,
            pageSize: 0
        }
    }).then(response => response.data)
}

export function exportTSumBalanceToPdf(fromDate: any, toDate: any) {
    return exportFile("/api/secured/accounting/exportTSumBalanceToPdf", null, {
        fromDate,
        toDate,
        offset: 0,
        pageSize: 0
    }).then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tbalancereport.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        //return downloadFile(response, 'tbalancereport.pdf')
        // TODO - Utilizar downloadFile para descargar el archivo y una función para poder implementarlo correctamente
    })
        .catch((error) => {
            console.error('Error al descargar el PDF:', error);
            throw error;
        });
}

export const downloadFile = async <T, D>(response: AxiosResponse<T, D>, filename: string = 'download') => {
    if (!(response instanceof Blob))
        return;

    const url = window.URL.createObjectURL(new Blob([response]));

    const a = document.createElement('a');
    a.href = url;

    a.download = filename
    a.style.display = 'none';

    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
}

export function getVATPurchases(_offset: number, _pageSize: number, params: any) {
    return axiosInstance.get(`/api/secured/accounting/getVATPurchases`, {
        params: {
            fromDate: params.fromDate,
            toDate: params.toDate,
            offset: 0,
            pageSize: 0
        }
    }).then(response => response.data)
}

export function exportVATPurchases(fromDate: any, toDate: any) {
    return axiosInstance.get(`/api/secured/accounting/exportVATPurchases`, {
        responseType: 'blob',
        params: {
            fromDate,
            toDate,
        }
    }).then(response => response.data)
}

export function getIntegrationBalance(_offset: number, _pageSize: number, params: any) {
    return axiosInstance.get(`/api/secured/accounting/getIntegrationBalance`, {
        params: {
            fromDate: params.fromDate,
            toDate: params.toDate,
            offset: 0,
            pageSize: 0
        }
    }).then(response => response.data)
}

export function exportIntegrationBalance(fromDate: any, toDate: any) {
    return axiosInstance.get(`/api/secured/accounting/exportIntegrationBalance`, {
        responseType: 'blob',
        params: {
            fromDate,
            toDate,
            offset: 0,
            pageSize: 0
        }
    }).then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'integration_balance_.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        // TODO - Refactor - Utilizar downloadFile para descargar el archivo y una función para poder implementarlo correctamente
    })
        .catch((error) => {
            console.error('Error al descargar el PDF:', error);
            throw error;
        });
}

export function generateAccountingClosureEntry(fromDate: any, toDate: any) {
    return axiosInstance.get(`/api/secured/accounting/generateAccountingClosureEntry`, {
        params: {
            fromDate,
            toDate
        }
    }).then(response => response.data)
}

export function generateAccountingOpeningEntry() {
    return axiosInstance.get(`/api/secured/accounting/generateAccountingOpeningEntry`, {
        params: {
        }
    }).then(response => response.data)
}

export function getLastAccountingDateOfOpeningEntry() {
    return axiosInstance.get(`/api/secured/accounting/getLastAccountingDateOfOpeningEntry`, {
        params: {
        }
    }).then(response => response.data)
}

export function getAliquotTotalizers(_offset: number, _pageSize: number, params: any) {
    return axiosInstance.get(`/api/secured/accounting/getAliquotTotalizers`, {
        params: {
            fromDate: params.fromDate,
            toDate: params.toDate,
            offset: 0,
            pageSize: 0
        }
    }).then(response => response.data)
}

export function useAccountingService() {
  return {
    findAAById,
    findAccountingById,
    getAllCompanies,
    getAllAccountings,
    addAccountingAccount,
    getAllAccountingAccountsById,
    getAllAccountingAccountsByLevel,
    getAllAccountingAccountsWithoutAllowedMovements,
    getAllAccountingAccountsByAccountingAccount,
    findParentAAByChildrenAA,
    castAccountingsToItems,
    registerMonthAccounting,
    getAllAccountingsPeriod,
    getLastAccountingPeriod,
    dateIsNotSameOrBeforeLastAccountingPeriod,
    getAccountingMovementTypes,
    findAccountingMovementTypeByAMIdAndAMDId,
    saveAccountingMovementDetail,
    saveAccountingMovement,
    removeAccountingMovement,
    getAccountingMovements,
    getAccountingMovementById,
    getAccountingMovementDetailsByAccountingMovementId,
    getTSumBalance,
    exportTSumBalanceToPdf,
    getVATPurchases,
    exportVATPurchases,
    getIntegrationBalance,
    exportIntegrationBalance,
    generateAccountingClosureEntry,
    generateAccountingOpeningEntry,
    getLastAccountingDateOfOpeningEntry,
    getAliquotTotalizers,
    downloadFile,
  }
}
